import { useInfiniteQuery } from '@tanstack/react-query';
import axios from 'axios';
import { tpoGenUrls } from 'config/urls';
import type { ChangeType, Frequency } from '../../../../types/calendar';
import dayjs from 'dayjs';
import { useEffect, useMemo } from 'react';
import get from 'lodash/get';
import type {
    CalendarBarColorConfig,
    CalendarBarFieldMap,
    CalendarColumnConfig,
} from '../../../../config/calendarColumnsConfig';
import type { LabeledOption } from '../../../../config/adapters/feConfigAdapter';
import { usePlanDetailsContext } from '../../../context/PlanDetailsContext';
import { buildBarColorIndex, resolveBarColors } from '../../utils/calendarBarColors';
import { resolveFieldLabel, type FieldValueLabelMaps } from '../../../../utils/resolveFieldLabel';

const PAGE_LIMIT = 50;

export type CalendarEventData = {
    event_name: string;
    event_id: string;
    event_status: string | null;
    source_event_id: string | null;
    promo_lifecycle_status: string | null;
    promo_name: string | null;
    promo_id: string | null;
    source_promo_id: string | null;
    promo_start_date: string | null;
    promo_end_date: string | null;
    promo_status: string | null;
    new_status: string | null;
    attributes: Record<string, string | null>;
    promotion_mechanic: string | null;
    keep_dates: boolean;
    keep_promo: boolean;
    keep_event: boolean;
    hover_data: {
        promo_status: string;
        attributes: Record<string, string | null>;
        promo_description: string | null;
        promotion_mechanic: string | null;
        promo_dates: string | null;
        products_count: number;
        products: any[];
        performance: {
            uplift: number | null;
            roi: number | null;
            incremental_vol: number | null;
            incremental_revenue: number | null;
        };
    };
    group_key: string | null;
};

export type PlanDetails = {
    plan_id: string;
    plan_name: string;
    planning_group_name: string;
    dim1_attrs: string[];
    dim2_attrs: string[];
    plan_year: number;
    baseline_vol_version: string;
    plan_version: string;
    created_by: string;
    updated_by: string;
    is_operational_plan: boolean;
};

type CalendarGroupData = {
    group_key: string | null;
    promo_name: string | null;
    promo_id: string | null;
    event_name: string;
    count: number;
    children: CalendarEventData[];
};

type CalendarDataResponse = {
    periods: { period_start_date: string; period_end_date: string }[];
    calendar_data: CalendarEventData[];
    grouped_data: CalendarGroupData[] | null;
    total_count: number;
    total_count_excluding_deleted: number;
    frequency: Frequency;
    plan_details: PlanDetails | null;
};

type CalendarEvent = {
    startDay: number;
    durationDays: number;
    tooltipValues: string[];
    color: string;
    bgColor: string;
    barLabelValues: string[];
    colorType: string;
    colorLabel: string;
};

function parseEventIntoPeriods(
    event: {
        startDate: string | null;
        endDate: string | null;
        tooltipValues: string[];
        color: string;
        bgColor: string;
        barLabelValues: string[];
        colorType: string;
        colorLabel: string;
    },
    periods: { period_start_date: string; period_end_date: string }[],
): Record<string, CalendarEvent[]> {
    const result: Record<string, CalendarEvent[]> = {};
    periods.forEach((p) => {
        result[p.period_start_date] = [];
    });

    if (!event.startDate || !event.endDate) return result;

    const eventStart = dayjs(event.startDate).startOf('day');
    const eventEnd = dayjs(event.endDate).startOf('day');

    if (!eventStart.isValid() || !eventEnd.isValid()) return result;

    const durationDays = eventEnd.diff(eventStart, 'day');

    for (const period of periods) {
        const colStart = dayjs(period.period_start_date).startOf('day');
        const colEnd = dayjs(period.period_end_date).startOf('day');

        if (!eventStart.isAfter(colEnd) && !eventStart.isBefore(colStart)) {
            result[period.period_start_date].push({
                startDay: eventStart.diff(colStart, 'day'),
                durationDays,
                tooltipValues: event.tooltipValues,
                color: event.color,
                bgColor: event.bgColor,
                barLabelValues: event.barLabelValues,
                colorType: event.colorType,
                colorLabel: event.colorLabel,
            });
            break;
        }
    }

    return result;
}

/** Maps one configured column onto a flat row field. See `buildColumnFieldResolvers`. */
type ColumnFieldResolver = {
    rowField: string;
    /** lodash paths into the API item, tried in order until one yields a value. */
    paths: string[];
    resolveLabelFrom?: string;
};

/**
 * antd reads a string `dataIndex` as a single flat key, so every configured
 * column needs its value copied to the top level of the row — including the ones
 * the API nests under `attributes.*`. `apiField` wins over the flat `dataIndex`
 * key, which stays as a fallback because the calendar response exposes some
 * attributes both flat and nested.
 */
const buildColumnFieldResolvers = (columns: CalendarColumnConfig[]): ColumnFieldResolver[] =>
    columns.map((col) => ({
        rowField: col.dataIndex,
        paths: col.apiField && col.apiField !== col.dataIndex ? [col.apiField, col.dataIndex] : [col.dataIndex],
        resolveLabelFrom: col.resolveLabelFrom,
    }));

function transformCalendarItem(
    item: CalendarEventData,
    periods: { period_start_date: string; period_end_date: string }[],
    fieldMap: CalendarBarFieldMap,
    colorIndex: Map<string, CalendarBarColorConfig>,
    labelMaps: FieldValueLabelMaps,
    columnFieldResolvers: ColumnFieldResolver[],
) {
    const barLabelValues = fieldMap.barLabelFields.map((entry) => {
        const raw = (get(item, entry.field) as string | null) ?? '';
        return resolveFieldLabel(raw, labelMaps, entry.resolveLabelFrom);
    });
    const tooltipValues = fieldMap.tooltipFields.map((entry) => {
        const raw = (get(item, entry.field) as string | null) ?? '';
        return resolveFieldLabel(raw, labelMaps, entry.resolveLabelFrom);
    });
    const colorFieldPath = fieldMap.colorField ?? fieldMap.barLabelFields[0]?.field;
    const colorKey = (get(item, colorFieldPath) as string | null) ?? null;
    const { label, color, bgColor } = resolveBarColors(colorKey, colorIndex);
    const periodEvents = parseEventIntoPeriods(
        {
            startDate: item.promo_start_date,
            endDate: item.promo_end_date,
            tooltipValues,
            color,
            bgColor,
            barLabelValues: barLabelValues,
            colorType: colorKey ?? '',
            colorLabel: label,
        },
        periods,
    );
    const columnValues: Record<string, string> = {};
    for (const resolver of columnFieldResolvers) {
        let raw: string | null | undefined;
        for (const path of resolver.paths) {
            raw = get(item, path) as string | null | undefined;
            if (raw) break;
        }
        columnValues[resolver.rowField] = resolver.resolveLabelFrom
            ? resolveFieldLabel(raw, labelMaps, resolver.resolveLabelFrom)
            : raw ?? '';
    }
    return {
        ...columnValues,
        key: item.promo_id ?? item.event_id,
        promo_name: item.promo_name ?? '',
        event_name: item.event_name,
        promo_id: item.promo_id,
        event_id: item.event_id,
        source_event_id: item.source_event_id ?? '',
        source_promo_id: item.source_promo_id ?? '',
        promo_status: item.promo_status,
        promo_lifecycle_status: item.promo_lifecycle_status as ChangeType | null,
        keep_dates: item.keep_dates ?? false,
        keep_promo: item.keep_promo ?? false,
        keep_event: item.keep_event ?? false,
        promo_start_date: item.promo_start_date,
        ...periodEvents,
    };
}

export const EMPTY_ROW_KEY = '__empty_row__';

export const isEmptyPlaceholderRow = (record: { key?: string }): boolean =>
    typeof record.key === 'string' && record.key.startsWith(EMPTY_ROW_KEY);

function generateFallbackPeriods(frequency: Frequency): { period_start_date: string; period_end_date: string }[] {
    const now = dayjs();
    const yearEnd = now.endOf('year');
    const periods: { period_start_date: string; period_end_date: string }[] = [];

    switch (frequency) {
        case 'W': {
            let cursor = now.startOf('week');
            while (cursor.isBefore(yearEnd) || cursor.isSame(yearEnd, 'day')) {
                periods.push({
                    period_start_date: cursor.format('YYYY-MM-DD'),
                    period_end_date: cursor.add(6, 'day').format('YYYY-MM-DD'),
                });
                cursor = cursor.add(7, 'day');
            }
            break;
        }
        case 'M': {
            let cursor = now.startOf('month');
            while (cursor.isBefore(yearEnd) || cursor.isSame(yearEnd, 'month')) {
                periods.push({
                    period_start_date: cursor.format('YYYY-MM-DD'),
                    period_end_date: cursor.endOf('month').format('YYYY-MM-DD'),
                });
                cursor = cursor.add(1, 'month');
            }
            break;
        }
        case 'Q': {
            const quarterStartMonth = Math.floor(now.month() / 3) * 3;
            let cursor = now.month(quarterStartMonth).startOf('month');
            while (cursor.isBefore(yearEnd) || cursor.isSame(yearEnd, 'month')) {
                const periodEnd = cursor.add(2, 'month').endOf('month');
                periods.push({
                    period_start_date: cursor.format('YYYY-MM-DD'),
                    period_end_date: periodEnd.format('YYYY-MM-DD'),
                });
                cursor = cursor.add(3, 'month');
            }
            break;
        }
    }

    return periods;
}

const EMPTY_ROW_COUNT = 15;

function createEmptyCalendarRows(periods: { period_start_date: string; period_end_date: string }[]) {
    const emptyPeriods: Record<string, CalendarEvent[]> = {};
    periods.forEach((p) => {
        emptyPeriods[p.period_start_date] = [];
    });

    return Array.from({ length: EMPTY_ROW_COUNT }, (_, i) => ({
        key: `${EMPTY_ROW_KEY}_${i}`,
        promo_name: '',
        promotion_mechanic: '',
        event_name: '',
        promo_id: '',
        event_id: '',
        source_event_id: '',
        source_promo_id: '',
        promo_status: '' as string | null,
        ...emptyPeriods,
    }));
}

const fetchCalendarData = async (
    planId: string,
    signal: AbortSignal,
    frequency: Frequency,
    searchKeyword: string,
    groupBy: string,
    selectedColumnKeys: string[],
    filters: Record<string, any>,
    offset: number,
    sortBy: string,
    sortingOrder: 'asc' | 'desc',
    labelMaps: FieldValueLabelMaps,
    groupByResolveLabelFrom?: string,
): Promise<CalendarDataResponse> => {
    const token = localStorage.getItem('accessToken');
    const response = await axios(tpoGenUrls.getCalendarData(planId), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
        },
        signal,
        data: {
            frequency,
            sort_by: sortBy,
            sorting_order: sortingOrder,
            limit: PAGE_LIMIT,
            offset,
            search_string: searchKeyword,
            search_fields: selectedColumnKeys,
            filters,
            group_by: groupBy === 'none' ? '' : groupBy,
        },
    });
    return {
        periods: response.data.periods,
        calendar_data: response.data.calendar_data,
        grouped_data: (response.data.grouped_data ?? []).map((_group: any) => ({
            group_key: _group.group_key,
            event_name: resolveFieldLabel(_group.group_label, labelMaps, groupByResolveLabelFrom),
            promo_id: _group.group_key,
            count: _group.count,
            children: _group.items,
        })),
        total_count: response.data.total_count,
        total_count_excluding_deleted: response.data.total_count_excluding_deleted,
        frequency,
        plan_details: response.data.plan_details ?? null,
    };
};

const useCalendarData = (
    planId: string,
    frequency: Frequency,
    searchKeyword: string,
    groupBy: string,
    selectedColumnKeys: string[],
    filters: Record<string, any>,
    sortBy: string,
    sortingOrder: 'asc' | 'desc',
    fieldMap: CalendarBarFieldMap,
    labelMaps: FieldValueLabelMaps,
    allDefaultColumns: CalendarColumnConfig[],
    calendarGroupByOptions: LabeledOption[],
) => {
    const { setCurrentPlanDetails } = usePlanDetailsContext();

    const columnFieldResolvers = useMemo(() => buildColumnFieldResolvers(allDefaultColumns), [allDefaultColumns]);

    const groupByResolveLabelFrom = useMemo(
        () => calendarGroupByOptions.find((option) => option.value === groupBy)?.resolveLabelFrom,
        [calendarGroupByOptions, groupBy],
    );

    const { data, isPending, isFetchingNextPage, error, hasNextPage, fetchNextPage, isFetching } = useInfiniteQuery({
        queryKey: [
            'calendarData',
            planId,
            frequency,
            searchKeyword,
            groupBy,
            selectedColumnKeys,
            filters,
            sortBy,
            sortingOrder,
        ],
        queryFn: ({ signal, pageParam }) =>
            fetchCalendarData(
                planId,
                signal,
                frequency,
                searchKeyword,
                groupBy,
                selectedColumnKeys,
                filters,
                pageParam,
                sortBy,
                sortingOrder,
                labelMaps,
                groupByResolveLabelFrom,
            ),
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
            const nextOffset = lastPageParam + PAGE_LIMIT;
            return nextOffset < lastPage.total_count ? nextOffset : undefined;
        },
    });

    const colorIndex = useMemo(() => buildBarColorIndex(fieldMap.colorMap), [fieldMap.colorMap]);

    const merged = useMemo(() => {
        const buildFallback = () => {
            const fallbackPeriods = generateFallbackPeriods(frequency);
            return { dataSource: createEmptyCalendarRows(fallbackPeriods), periods: fallbackPeriods };
        };

        if (!data?.pages?.length) return buildFallback();

        const firstPage = data.pages[0];
        if (!firstPage) return buildFallback();

        const periods = firstPage.periods;
        const hasGroupedData = (firstPage.grouped_data?.length ?? 0) > 0;

        if (hasGroupedData) {
            const groupMap = new Map<string, CalendarGroupData>();
            for (const page of data.pages) {
                for (const group of page.grouped_data ?? []) {
                    const mapKey = group.group_key ?? '__ungrouped__';
                    const existing = groupMap.get(mapKey);
                    if (existing) {
                        existing.children = [...existing.children, ...group.children];
                        existing.count += group.count;
                    } else {
                        groupMap.set(mapKey, { ...group });
                    }
                }
            }

            const dataSource = Array.from(groupMap.values()).map((group) => {
                const emptyPeriods: Record<string, CalendarEvent[]> = {};
                periods?.forEach((p: { period_start_date: string }) => {
                    emptyPeriods[p.period_start_date] = [];
                });
                return {
                    key: group.group_key ?? group.event_name,
                    promo_name: group.promo_name ?? '',
                    promotion_mechanic: '',
                    event_name: group.event_name,
                    promo_id: group.promo_id ?? null,
                    event_id: group.group_key ?? group.event_name,
                    source_event_id: '',
                    source_promo_id: '',
                    promo_status: null as string | null,
                    is_group: true,
                    ...emptyPeriods,
                    children: group.children.map((child: CalendarEventData) =>
                        transformCalendarItem(child, periods, fieldMap, colorIndex, labelMaps, columnFieldResolvers),
                    ),
                };
            });
            return { dataSource, periods };
        }

        const dataSource = data.pages.flatMap((page) =>
            (page.calendar_data ?? []).map((item: CalendarEventData) =>
                transformCalendarItem(item, periods, fieldMap, colorIndex, labelMaps, columnFieldResolvers),
            ),
        );

        if (!dataSource.length) {
            const effectivePeriods = periods?.length ? periods : generateFallbackPeriods(frequency);
            return { dataSource: createEmptyCalendarRows(effectivePeriods), periods: effectivePeriods };
        }

        return { dataSource, periods };
    }, [data, frequency, fieldMap, colorIndex, labelMaps, columnFieldResolvers]);

    const planDetailsFromData = data?.pages?.[0]?.plan_details ?? null;
    useEffect(() => {
        setCurrentPlanDetails(planDetailsFromData);
    }, [planDetailsFromData, setCurrentPlanDetails]);

    const totalCount = data?.pages?.[0]?.total_count ?? 0;
    const loadedCount = (merged.dataSource as any[]).reduce((acc: number, row: any) => {
        if (typeof row.key === 'string' && row.key.startsWith(EMPTY_ROW_KEY)) return acc;
        return acc + (row.children?.length ?? 1);
    }, 0);

    return {
        dataSource: merged.dataSource,
        periods: merged.periods,
        isPending,
        isFetching,
        isFetchingNextPage,
        hasNextPage: hasNextPage ?? false,
        fetchNextPage,
        totalCount,
        loadedCount,
        error,
    };
};

export default useCalendarData;
